import React, { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import PhoneInput, { isValidPhoneNumber, type Value } from "react-phone-number-input";
import { IconDownload, IconFileText, IconLoader2, IconPhoneCall, IconTrash, IconUpload } from "@tabler/icons-react";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppDispatch } from "@/store";
import { requiredDocuments } from "@/lib/required-documents";
import { ACCEPTED_DOCUMENT_TYPES, formatFileSize } from "@/app/booking-calendar/api/documentsApi";
import {
  useCancelBookingApprovalMutation,
  useCreateBookingApprovalMutation,
  useGetBookingApprovalModeQuery,
  useGetBookingApprovalQuery,
  useVerifyBookingApprovalMutation,
} from "@/app/booking-flow/publicBookingFlowApi";
import {
  isCodeApproval,
  isPhoneApprovalPending,
  type BookingApprovalChannelMode,
  type BookingApprovalStatus,
} from "@/app/booking-flow/publicBookingFlowTypes";
import {
  ApprovalCodeEntry,
  readVerifyFailure,
  type ApprovalCodeError,
} from "@/app/booking-flow/components/ApprovalCodeEntry";
import { BOOKING_TIME_ZONE } from "@/app/booking-flow/bookingTimeZone";
import {
  GENERIC_ERROR_MESSAGE,
  GRANT_EXPIRED_MESSAGE,
  LOCKED_OUT_MESSAGE,
  accessErrorMessage,
  documentsAvailability,
  downloadErrorMessage,
  downloadPublicBookingDocument,
  failureCode,
  failureStatus,
  isNoGrant,
  publicDocumentsApi,
  removeErrorMessage,
  sizeLimitSentence,
  uploadErrorMessage,
  uploadLimitBytes,
  useAccessPublicBookingDocumentsMutation,
  useDeletePublicBookingDocumentMutation,
  useGetPublicBookingDocumentsQuery,
  useUploadPublicBookingDocumentMutation,
  type PublicBookingDocument,
  type PublicBookingDocuments,
} from "./publicDocumentsApi";

const BRAND_BUTTON = "w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]";

function formatAppointment(startIso: string): string {
  return new Date(startIso).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: BOOKING_TIME_ZONE,
  });
}

function formatUploaded(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: BOOKING_TIME_ZONE,
  });
}

/** The one shared what-to-bring list (AB#598), at the top of the page in every state. */
function WhatToBring() {
  const documents = requiredDocuments();
  return (
    <Card className="max-w-2xl w-full">
      <CardHeader>
        <CardTitle>Document Upload</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-slate-600 space-y-4">
        <p className="font-semibold text-slate-900">{documents.intro}</p>
        <ul className="space-y-3 list-disc list-inside text-slate-700">
          {documents.full.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * /booking/document-upload (AB#774). Always the what-to-bring list first. Below it, when customer
 * documents are on (learned from the list endpoint's answer: see documentsAvailability):
 * - with the manage link's `?t=` token, the confirmation-number form (and, with the server's phone
 *   challenge on, a call to the phone on the appointment), then the appointment's documents;
 * - without one, where to find the link.
 * With the flags off, or while that cannot be told, the list alone, as before.
 */
export default function DocumentUploadPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") ?? "";

  const list = useGetPublicBookingDocumentsQuery(undefined, { refetchOnMountOrArgChange: true });
  const availability = documentsAvailability(list);
  // Not `currentData` alone: RTK Query keeps the last data through a failed refetch, and a failed
  // refetch here (404 noGrant) is exactly the grant having lapsed.
  const granted = list.isError ? undefined : list.currentData;

  // Once the panel has been shown, losing it means the grant lapsed (or the appointment stopped being
  // upcoming), which the code form says rather than starting over silently.
  const [hadGrant, setHadGrant] = useState(false);
  useEffect(() => {
    if (granted) setHadGrant(true);
  }, [granted]);

  let body: React.ReactNode;
  if (availability === "checking") {
    body = <Skeleton className="max-w-2xl w-full h-40" />;
  } else if (availability === "off") {
    body = (
      <Card className="max-w-2xl w-full">
        <CardContent className="text-sm text-slate-600 pt-6">
          <p data-testid="documents-unavailable">
            Online document upload isn't available right now. Please bring printed copies of your required documents to
            your appointment instead.
          </p>
        </CardContent>
      </Card>
    );
  } else if (granted) {
    body = <DocumentsPanel granted={granted} />;
  } else if (token === "") {
    body = (
      <Card className="max-w-2xl w-full">
        <CardContent className="text-sm text-slate-600 pt-6 space-y-2" data-testid="documents-need-link">
          <p>
            You can send us copies of your documents online before your appointment. Open the link in your appointment
            confirmation or reminder text or email, then enter your confirmation number.
          </p>
          <p>If you can't find the link, please bring printed copies of your documents to your appointment.</p>
        </CardContent>
      </Card>
    );
  } else {
    const notice =
      failureCode(list.error) === "notUpcoming"
        ? accessErrorMessage(list.error)
        : hadGrant && isNoGrant(list.error)
          ? GRANT_EXPIRED_MESSAGE
          : "";
    body = <AccessFlow token={token} notice={notice} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex flex-col items-center gap-4 px-4 sm:px-6 lg:px-8 py-8">
        <WhatToBring />
        {body}
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}

// "code" is the confirmation-number form; "approvalCode" is waiting for the one-time code sent to the
// phone (BookingApproval:Mode=Code, AB#585), which is never polled.
type ApprovalStep = "code" | "phone" | "waiting" | "approvalCode" | "rejected" | "expired" | "failed";

/**
 * Earning the grant: the confirmation number, then (only when the server answers 403 approvalRequired,
 * which is the phone challenge being on) the phone number on the appointment, a call to it, and a poll
 * until it is approved. An approved approval is kept only a few minutes, so access is asked for again
 * the moment the poll reports it.
 */
function AccessFlow({ token, notice }: { token: string; notice: string }) {
  const [access, { isLoading: accessing }] = useAccessPublicBookingDocumentsMutation();
  const [createApproval, { isLoading: creating }] = useCreateBookingApprovalMutation();
  const [cancelApproval] = useCancelBookingApprovalMutation();
  const [verifyApproval, { isLoading: verifying }] = useVerifyBookingApprovalMutation();

  const [codeInput, setCodeInput] = useState("");
  const [phoneInput, setPhoneInput] = useState<Value | undefined>();
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<ApprovalStep>("code");

  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [pollSeconds, setPollSeconds] = useState(3);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const usedApproval = useRef<string | null>(null);

  // Code mode (AB#585): how the current approval is answered, from its 202 (the mode query only words
  // the phone form before there is one), and the verify endpoint's answer, which stands in for the
  // poll's in the effect below.
  const { data: approvalModeInfo } = useGetBookingApprovalModeQuery();
  const [approvalChannel, setApprovalChannel] = useState<BookingApprovalChannelMode | null>(null);
  const codeChannel = (approvalChannel ?? approvalModeInfo?.approvalMode) === "code";
  const [verified, setVerified] = useState<BookingApprovalStatus | null>(null);
  const [codeError, setCodeError] = useState<ApprovalCodeError | undefined>(undefined);

  const code = codeInput.trim();
  const phoneValid = !!phoneInput && isValidPhoneNumber(phoneInput);
  const locallyExpired = expiresAt !== null && now > expiresAt;

  useEffect(() => {
    if (step !== "waiting" && step !== "approvalCode") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [step]);

  const { data: approval, isError: approvalError } = useGetBookingApprovalQuery(
    step === "waiting" && approvalId ? approvalId : skipToken,
    { pollingInterval: step === "waiting" && !locallyExpired ? pollSeconds * 1000 : 0, skipPollingIfUnfocused: false },
  );

  const requestAccess = async (withApproval?: string): Promise<void> => {
    setError("");
    try {
      // On success the list's cache takes the answer and the page shows the panel.
      await access({ token, confirmationCode: code, approvalId: withApproval }).unwrap();
    } catch (failure) {
      if (failureStatus(failure) === 403 && failureCode(failure) === "approvalRequired") {
        if (withApproval) {
          // The approval was seen approved but is no longer accepted: it lapsed in between.
          setStep("expired");
        } else {
          setStep("phone");
        }
        return;
      }
      setStep("code");
      setError(accessErrorMessage(failure));
    }
  };

  // The poll's answer, or in code mode the verify endpoint's. Approved: ask for access at once with it.
  // Anything else final: say so.
  useEffect(() => {
    if (step !== "waiting" && step !== "approvalCode") return;
    if (approvalError || locallyExpired) {
      setStep("expired");
      return;
    }
    const answer = step === "approvalCode" ? verified : approval;
    if (!answer || answer.state === "pending") return;
    if (answer.state === "approved") {
      if (usedApproval.current === answer.approvalId) return;
      usedApproval.current = answer.approvalId;
      void requestAccess(answer.approvalId);
      return;
    }
    setStep(answer.state === "rejected" ? "rejected" : answer.state === "failed" ? "failed" : "expired");
    // requestAccess is recreated each render and only reads current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval, verified, approvalError, locallyExpired, step]);

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code === "") return;
    await requestAccess();
  };

  const handlePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setPhoneTouched(true);
      return;
    }
    await sendApproval();
  };

  // Asks the server to reach the phone typed on the phone form. Also what "Send a new code" repeats.
  // Reports whether the request went through. A failed resend passes stayOnFailure, so the code view
  // and the still-valid approval behind it survive with the error shown inline.
  const sendApproval = async (stayOnFailure = false): Promise<boolean> => {
    setError("");
    try {
      const result = await createApproval({ phoneNumber: phoneInput!, confirmationCode: code, action: "ViewBooking" }).unwrap();
      if (!isPhoneApprovalPending(result)) {
        // The challenge was switched off in between: the code alone is enough again.
        await requestAccess();
        return true;
      }
      setApprovalId(result.approvalId);
      setPollSeconds(result.pollIntervalSeconds);
      setExpiresAt(Date.parse(result.expiresAt));
      setNow(Date.now());
      setApprovalChannel(isCodeApproval(result) ? "code" : "call");
      setVerified(null);
      setCodeError(undefined);
      setStep(isCodeApproval(result) ? "approvalCode" : "waiting");
      return true;
    } catch (failure) {
      const message = failureStatus(failure) === 429 ? LOCKED_OUT_MESSAGE : GENERIC_ERROR_MESSAGE;
      if (stayOnFailure) {
        setCodeError({ kind: "other", message });
      } else {
        setStep("phone");
        setError(message);
      }
      return false;
    }
  };

  // Code mode: a 200 goes to the effect above; a wrong code stays here with the guesses left; an
  // approval that is over (410, 404, or the last guess used) shows the expired view; a 409 means
  // approvals went back to calls under the page, so it waits for the call instead.
  const verifyCode = async (typed: string) => {
    if (!approvalId) return;
    setCodeError(undefined);
    try {
      setVerified(await verifyApproval({ approvalId, code: typed }).unwrap());
    } catch (failure) {
      const outcome = readVerifyFailure(failure);
      if (outcome.kind === "gone") {
        setStep("expired");
      } else if (outcome.kind === "notCodeMode") {
        setApprovalChannel("call");
        setStep("waiting");
      } else {
        setCodeError(outcome);
      }
    }
  };

  // "Send a new code": the phone form's request again, to the number already typed, then the old
  // approval withdrawn (the new one has already superseded it; this only tidies up). Withdrawn only once
  // the new request succeeds: a refused one (a 429) leaves the old code usable, error shown inline.
  const resendCode = async () => {
    const previous = approvalId;
    setCodeError(undefined);
    if ((await sendApproval(true)) && previous) {
      void cancelApproval(previous);
    }
  };

  // Withdrawn, not just abandoned: an unanswered approval call is tried again, and nobody is waiting.
  const stopWaiting = () => {
    const withdrawing = approvalId;
    setApprovalId(null);
    setExpiresAt(null);
    setStep("phone");
    if (withdrawing) void cancelApproval(withdrawing);
  };

  const startOver = () => {
    setApprovalId(null);
    setExpiresAt(null);
    setError("");
    setStep("code");
  };

  if (step === "waiting") {
    const remaining = expiresAt === null ? 0 : Math.max(0, Math.ceil((expiresAt - now) / 1000));
    return (
      <Card key="waiting" className="max-w-2xl w-full" data-testid="documents-waiting">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-sky-100 flex items-center justify-center rounded-full">
            <IconPhoneCall className="w-8 h-8 text-sky-600" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl font-semibold">Answer Your Phone</CardTitle>
          <CardDescription className="text-base">
            We're calling the phone number on this appointment. When you answer, press <strong>1</strong> to see your
            documents, or <strong>2</strong> to deny.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3" aria-live="polite">
          <IconLoader2 className="w-6 h-6 animate-spin text-slate-400" aria-hidden="true" />
          <p className="text-sm text-slate-500">
            This request expires in {Math.floor(remaining / 60)}:{(remaining % 60).toString().padStart(2, "0")}
          </p>
        </CardContent>
        <CardFooter>
          <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={stopWaiting}>
            Cancel this request
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (step === "approvalCode") {
    return (
      <ApprovalCodeEntry
        key={`code:${approvalId}`}
        className="max-w-2xl w-full"
        action="ViewBooking"
        expiresAt={expiresAt}
        nowMs={now}
        onVerify={verifyCode}
        onResend={phoneValid ? () => void resendCode() : undefined}
        onStartOver={stopWaiting}
        error={codeError}
        busy={verifying || creating || accessing}
      />
    );
  }

  if (step === "rejected" || step === "expired" || step === "failed") {
    return (
      <Card key="resolved" className="max-w-2xl w-full" data-testid="documents-approval-result">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">
            {step === "rejected"
              ? "Request Denied"
              : step === "failed"
                ? "Something Went Wrong"
                : approvalChannel === "code"
                  ? "Code No Longer Valid"
                  : "No Response"}
          </CardTitle>
          <CardDescription className="text-base">
            {step === "rejected"
              ? "The request was denied from the phone on this appointment."
              : step === "failed"
                ? "The request was approved, but we could not finish it. Please try again, or call us so we can help."
                : approvalChannel === "code"
                  ? "That code can no longer be used. Check the phone number and confirmation number, then try again."
                  : "We did not get an answer in time. Check the phone number and confirmation number, then try again."}
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button type="button" className={BRAND_BUTTON} onClick={startOver}>
            Try Again
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (step === "phone") {
    return (
      <Card key="phone" className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">Confirm It's You</CardTitle>
          <CardDescription>
            {codeChannel ? (
              "For your security, we'll send a six-digit code to the phone number on this appointment. Enter that phone number."
            ) : (
              <>
                For your security, we'll call the phone number on this appointment. Enter that phone number, then answer the
                call and press 1.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" id="documents-phone-form" onSubmit={handlePhone}>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <PhoneInput
                id="phone"
                name="phone"
                placeholder="Enter phone number"
                defaultCountry="US"
                value={phoneInput}
                onChange={(value) => setPhoneInput(value)}
                onBlur={() => setPhoneTouched(true)}
                className="border-input [&.PhoneInput--focus]:border-ring [&.PhoneInput--focus]:ring-ring/50 [&.PhoneInput--focus]:ring-[3px] flex h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none md:text-sm"
                numberInputProps={{
                  className: "placeholder:text-muted-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none md:text-sm",
                }}
              />
              {phoneTouched && !phoneValid && <FieldError>Enter a valid phone number.</FieldError>}
            </div>
            {error !== "" && <FieldError>{error}</FieldError>}
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button form="documents-phone-form" type="submit" className={BRAND_BUTTON} disabled={creating || accessing}>
            {codeChannel ? (creating ? "Sending…" : "Send Code") : creating ? "Calling…" : "Call Me"}
          </Button>
          <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={startOver}>
            Back
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card key="code" className="max-w-2xl w-full">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-semibold">Send Your Documents</CardTitle>
        <CardDescription>
          For your security, enter the 6-digit confirmation number from your appointment text message or email.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" id="documents-code-form" onSubmit={handleCode}>
          {notice !== "" && error === "" && (
            <p role="status" className="text-sm text-slate-700" data-testid="documents-notice">
              {notice}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="code">
              Confirmation Number <span aria-hidden="true">*</span>
            </Label>
            <Input
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit confirmation number"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              required
            />
          </div>
          {error !== "" && <FieldError>{error}</FieldError>}
        </form>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button form="documents-code-form" type="submit" className={BRAND_BUTTON} disabled={accessing || code === ""}>
          {accessing ? (
            <span className="flex items-center gap-2">
              <IconLoader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Checking…
            </span>
          ) : (
            "Continue"
          )}
        </Button>
        <Button asChild variant="ghost" className="w-full text-slate-400">
          <Link to="/booking">Back to Scheduler Home</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * The granted appointment's documents: each one's name, size and upload time, a Pending badge until the
 * scan has cleared it, Download only for their own cleared upload, Remove (after a confirm) only for
 * their own upload, and an upload button. A refusal meaning the grant lapsed sends the page back to the
 * code form, through the list refetching (every mutation invalidates it).
 */
function DocumentsPanel({ granted }: { granted: PublicBookingDocuments }) {
  const dispatch = useAppDispatch();
  const [upload, { isLoading: uploading }] = useUploadPublicBookingDocumentMutation();
  const [remove] = useDeletePublicBookingDocumentMutation();
  const fileInput = useRef<HTMLInputElement>(null);
  const maxBytes = uploadLimitBytes(granted);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again still fires a change.
    event.target.value = "";
    if (!file || uploading) return;

    setUploadError(null);
    setUploaded(null);
    if (file.size > maxBytes) {
      setUploadError(uploadErrorMessage({ status: 413, data: { code: "tooLarge" } }, maxBytes));
      return;
    }

    const result = await upload(file);
    if ("error" in result) {
      setUploadError(uploadErrorMessage(result.error, maxBytes));
    } else {
      setUploaded(result.data.fileName);
    }
  }

  async function download(document: PublicBookingDocument) {
    if (!document.canDownload || downloadingId) return;
    setDownloadingId(document.id);
    setRowError(null);
    try {
      await downloadPublicBookingDocument(document);
    } catch (error) {
      if (isNoGrant(error)) {
        dispatch(publicDocumentsApi.util.invalidateTags([{ type: "PublicBookingDocuments", id: "granted" }]));
      } else {
        setRowError(downloadErrorMessage(error));
      }
    } finally {
      setDownloadingId(null);
    }
  }

  async function confirmRemove(document: PublicBookingDocument) {
    if (removingId) return;
    setRemovingId(document.id);
    setRowError(null);
    const result = await remove(document.id);
    setRemovingId(null);
    setConfirmingId(null);
    if ("error" in result && !isNoGrant(result.error)) {
      setRowError(removeErrorMessage(result.error));
    }
  }

  return (
    <Card className="max-w-2xl w-full" data-testid="documents-panel">
      <CardHeader>
        <CardTitle>Your Documents</CardTitle>
        <CardDescription>
          For your appointment on {formatAppointment(granted.start)}
          {granted.siteName ? ` at ${granted.siteName}` : ""}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {granted.documents.length === 0 ? (
          <p className="text-slate-500" data-testid="documents-empty">
            No documents yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {granted.documents.map((document) => {
              const confirming = confirmingId === document.id;
              return (
                <li key={document.id} className="rounded-md border px-3 py-2" data-testid="document-row">
                  <div className="flex items-center gap-2">
                    <IconFileText className="size-4 text-slate-400 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-medium" title={document.fileName}>
                      {document.fileName}
                    </span>
                    {document.pending && (
                      <Badge variant="secondary" title="Being checked. You can download it once the check finishes.">
                        Pending
                      </Badge>
                    )}
                    {document.canDownload && (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Download ${document.fileName}`}
                        disabled={downloadingId !== null}
                        onClick={() => download(document)}
                      >
                        <IconDownload aria-hidden="true" />
                      </Button>
                    )}
                    {document.canRemove && !confirming && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="hover:text-red-700"
                        aria-label={`Remove ${document.fileName}`}
                        disabled={removingId !== null}
                        onClick={() => {
                          setRowError(null);
                          setConfirmingId(document.id);
                        }}
                      >
                        <IconTrash aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {formatFileSize(document.sizeBytes)} · uploaded {formatUploaded(document.uploadedAt)}
                  </div>
                  {confirming && (
                    <div role="group" aria-label="Confirm removal" className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span>Remove this document? This cannot be undone.</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={removingId !== null}
                        data-testid="confirm-remove"
                        onClick={() => confirmRemove(document)}
                      >
                        {removingId === document.id ? "Removing…" : "Remove"}
                      </Button>
                      <Button size="sm" variant="ghost" disabled={removingId !== null} onClick={() => setConfirmingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {rowError && (
          <p role="alert" className="text-red-700">
            {rowError}
          </p>
        )}

        <div className="space-y-2">
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_DOCUMENT_TYPES}
            className="hidden"
            aria-label="Choose a document to upload"
            data-testid="document-input"
            onChange={chooseFile}
          />
          <Button type="button" className={BRAND_BUTTON} disabled={uploading} onClick={() => fileInput.current?.click()}>
            <IconUpload aria-hidden="true" />
            {uploading ? "Uploading…" : "Upload a Document"}
          </Button>
          <p className="text-xs text-slate-500" data-testid="upload-limits">
            PDF or plain-text files only. {sizeLimitSentence(maxBytes)} Each upload is checked for viruses before it
            can be opened.
          </p>
          {uploadError && (
            <p role="alert" className="text-red-700" data-testid="upload-error">
              {uploadError}
            </p>
          )}
          {uploaded && (
            <p role="status" className="text-emerald-700">
              {uploaded} was uploaded.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
